import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge, Button, Card, Input, Select, Typography, Space, message as antMessage } from 'antd';
import { MessageOutlined, SendOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { beepMessage, buzz } from '../utils/beep';

const { Text } = Typography;

// Floating "Live Chat with Admin" button in the bottom-right corner of every
// staff page: one click and they are talking to the admin without leaving the page.
export default function ChatWidget({ user }) {
  const [open, setOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [peerId, setPeerId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [unread, setUnread] = useState(0);
  const [sending, setSending] = useState(false);
  const lastIdRef = useRef(0);
  const bottomRef = useRef(null);

  const loadAdmins = useCallback(async () => {
    try {
      const res = await api.get('/chat/people');
      const list = (Array.isArray(res.data) ? res.data : [])
        .filter(p => ['super_admin', 'admin', 'manager', 'hr_manager'].includes(p.role));
      setAdmins(list);
      setUnread(list.reduce((s, p) => s + (p.unread || 0), 0));
      setPeerId(prev => prev || (list.find(p => p.unread > 0) || list[0])?.id || null);
    } catch (err) { /* ignore */ }
  }, []);

  const loadMessages = useCallback(async (reset) => {
    if (!peerId) return;
    try {
      const res = await api.get('/chat/messages', {
        params: { peer_id: peerId, after_id: reset ? 0 : lastIdRef.current }
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      if (rows.length > 0) {
        lastIdRef.current = Number(rows[rows.length - 1].id);
        const incoming = rows.some(r => Number(r.from_user_id) !== Number(user.id));
        setMessages(prev => (reset ? rows : [...prev, ...rows]));
        if (incoming && !reset) { beepMessage(); buzz(); }
      } else if (reset) {
        setMessages([]);
      }
    } catch (err) { /* ignore */ }
  }, [peerId, user.id]);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);

  useEffect(() => {
    if (!open || !peerId) return;
    lastIdRef.current = 0;
    loadMessages(true);
  }, [open, peerId, loadMessages]);

  useEffect(() => {
    const t = setInterval(() => {
      loadAdmins();
      if (open) loadMessages(false);
    }, 5000);
    return () => clearInterval(t);
  }, [open, loadAdmins, loadMessages]);

  useEffect(() => { if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, open]);

  const send = async () => {
    const body = text.trim();
    if (!body || !peerId) return;
    setSending(true);
    try {
      await api.post('/chat/messages', { to_user_id: peerId, message: body });
      setText('');
      await loadMessages(false);
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'Message bhej nahi paye');
    }
    setSending(false);
  };

  const peer = admins.find(a => a.id === peerId);

  if (!open) {
    return (
      <Badge count={unread} offset={[-8, 8]}>
        <Button
          type="primary"
          size="large"
          shape="round"
          icon={<MessageOutlined />}
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed', right: 24, bottom: 90, zIndex: 1200,
            boxShadow: '0 6px 20px rgba(79,70,229,0.45)',
            background: 'linear-gradient(135deg,#667eea,#4f46e5)', border: 'none'
          }}
        >
          Live Chat with Admin
        </Button>
      </Badge>
    );
  }

  return (
    <Card
      size="small"
      title={
        <Space size={6}>
          <MessageOutlined style={{ color: '#4f46e5' }} />
          <Text strong style={{ fontSize: 13 }}>Live Chat with Admin</Text>
        </Space>
      }
      extra={<Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setOpen(false)} />}
      style={{
        position: 'fixed', right: 24, bottom: 90, zIndex: 1200, width: 330,
        boxShadow: '0 10px 32px rgba(60,70,120,0.28)', borderRadius: 14
      }}
      styles={{ body: { padding: 10 } }}
    >
      {admins.length > 1 && (
        <Select
          size="small"
          style={{ width: '100%', marginBottom: 8 }}
          value={peerId}
          onChange={setPeerId}
          options={admins.map(a => ({
            value: a.id,
            label: `${a.full_name}${a.online ? ' (online)' : ''}${a.unread ? ` — ${a.unread} new` : ''}`
          }))}
        />
      )}
      {admins.length === 0 && <Text type="secondary" style={{ fontSize: 12 }}>Koi admin available nahi</Text>}

      <div style={{ height: 260, overflowY: 'auto', background: '#fafbff', borderRadius: 8, padding: 8 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', paddingTop: 100 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {peer ? `${peer.full_name} se baat karo` : 'Message likho'}
            </Text>
          </div>
        )}
        {messages.map(m => {
          const mine = Number(m.from_user_id) === Number(user.id);
          return (
            <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 6 }}>
              <div style={{
                maxWidth: '80%', fontSize: 12, padding: '6px 10px', borderRadius: 10,
                background: mine ? '#4f46e5' : '#fff', color: mine ? '#fff' : '#222',
                border: mine ? 'none' : '1px solid #e6e8f0'
              }}>
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.message}</div>
                <div style={{ fontSize: 9, opacity: 0.7, textAlign: 'right' }}>{dayjs(m.created_at).format('hh:mm A')}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <Space.Compact style={{ width: '100%', marginTop: 8 }}>
        <Input
          size="small"
          placeholder="Message likho..."
          value={text}
          onChange={e => setText(e.target.value)}
          onPressEnter={send}
          disabled={sending || !peerId}
        />
        <Button size="small" type="primary" icon={<SendOutlined />} onClick={send} loading={sending} disabled={!peerId} />
      </Space.Compact>
    </Card>
  );
}
